'use client';

import { type ReactNode, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Checkbox } from '@kit/ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@kit/ui/drawer';

import { useBookingPresentation } from './booking-presentation';
import { BookingTermsText } from './booking-terms-text';

export function BookingPolicyNotice({
  visible = true,
  requireAcceptance,
  accepted,
  onAcceptedChange,
  cancellationPolicyItems,
  termsText,
  summary,
  children,
}: {
  visible?: boolean;
  requireAcceptance: boolean;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
  cancellationPolicyItems: string[];
  termsText: string;
  /** One-line rule for this booking (e.g. the class's cancellation window). */
  summary?: ReactNode;
  children: ReactNode;
}) {
  const { t, i18n } = useTranslation('bookings');
  const presentation = useBookingPresentation();
  const [openPolicy, setOpenPolicy] = useState<'cancellation' | 'terms' | null>(
    null,
  );

  if (!visible) return children;

  const links = (
    <>
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault();
          setOpenPolicy('cancellation');
        }}
        className="text-primary hover:text-primary/80 font-medium underline underline-offset-2"
      >
        {t('cancellationPolicy')}
      </button>{' '}
      {t('and')}{' '}
      {termsText ? (
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            setOpenPolicy('terms');
          }}
          className="text-primary hover:text-primary/80 font-medium underline underline-offset-2"
        >
          {t('termsAndConditions')}
        </button>
      ) : (
        <a
          href="/terms-of-service"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:text-primary/80 font-medium underline underline-offset-2"
        >
          {t('termsAndConditions')}
        </a>
      )}
      .
    </>
  );

  return (
    <>
      {requireAcceptance && summary && (
        <div className="text-muted-foreground text-start text-xs leading-5">
          {summary}
        </div>
      )}
      {requireAcceptance && (
        <label
          data-booking-policy
          className="text-muted-foreground flex items-start gap-2 text-start text-xs leading-5"
        >
          <Checkbox
            checked={accepted}
            onCheckedChange={(checked) => onAcceptedChange(checked === true)}
            className="mt-0.5 shrink-0"
            data-test="booking-policy-acceptance"
          />
          <span className="min-w-0 flex-1">
            {t('acceptPolicyPrefix', { defaultValue: 'I agree to the' })}{' '}
            {links}
          </span>
        </label>
      )}
      {children}
      {!requireAcceptance && (
        <div className="text-muted-foreground space-y-1 text-center text-xs leading-5">
          {summary}
          <p>
            {t('byBookingAgree')} {links}
          </p>
        </div>
      )}
      <Drawer
        open={openPolicy !== null}
        onOpenChange={(open) => {
          if (!open) setOpenPolicy(null);
        }}
      >
        <DrawerContent
          {...presentation}
          dir={i18n.dir()}
          aria-describedby={undefined}
        >
          <DrawerHeader className="px-6">
            <DrawerTitle>
              {t(
                openPolicy === 'terms'
                  ? 'termsAndConditions'
                  : 'cancellationPolicyTitle',
              )}
            </DrawerTitle>
          </DrawerHeader>
          <div className="max-h-[55vh] overflow-y-auto px-6 pb-6">
            {openPolicy === 'terms' ? (
              <BookingTermsText text={termsText} />
            ) : (
              <ul className="text-muted-foreground list-disc space-y-3 ps-5 text-sm">
                {cancellationPolicyItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-background sticky bottom-0 px-6 pt-2 pb-8">
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full touch-manipulation"
              onClick={() => setOpenPolicy(null)}
            >
              {t('close')}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
